import { DragDropContentView } from 'expo-drag-drop-content-view'
import * as FileSystem from 'expo-file-system/legacy'
import * as ImagePicker from 'expo-image-picker'
import { memo, useState } from 'react'
import { Platform, Pressable, View } from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import FosterThumbnail from '@/components/fosters/FosterThumbnail'
import Snackbar from '@/components/snackbar/Snackbar'
import UniActivityIndicator from '@/components/unistyles/UniActivityIndicator'
import UniIcon from '@/components/unistyles/UniIcon'
import UniLinearGradient from '@/components/unistyles/UniLinearGradient'
import Species from '@/enums/species'
import FosterService from '@/services/fosterService'
import { useAppStore } from '@/store/useAppStore'
import { getAccessToken } from '@/utils/auth/auth'
import { ApiRoot } from '../../../constants'

interface Props {
  disabled: boolean
  fosterId: string
}

const FosterThumbnailEdit = ({ disabled, fosterId }: Props) => {
  const { theme } = useUnistyles()

  const [isThumbnailUploading, setIsThumbnailUploading] = useState(false)
  const [isFileDragging, setIsFileDragging] = useState(false)

  const currentOrganization = useAppStore.use.currentOrganization().organization

  const { foster, refreshFoster } = FosterService.useFoster(fosterId)

  const { fosterList, refreshFosterList } = FosterService.useFosterList()

  const { blurhash, name, species, thumbUri } =
    foster || fosterList.find((f) => f.id === fosterId) || {}

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      allowsEditing: false,
      allowsMultipleSelection: false,
      exif: true,
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      selectionLimit: 1,
    })

    if (!result.canceled) {
      const file = result.assets[0]

      if (!file) {
        return
      }

      upload({
        fileName: file.fileName,
        size: file.fileSize || 0,
        type: file.mimeType,
        uri: file.uri,
      })
    }
  }

  const upload = async (file: {
    fileName?: string | null
    size: number
    type?: string
    uri: string
  }) => {
    setIsThumbnailUploading(true)

    let fileBlob: Blob | undefined
    if (Platform.OS === 'web') {
      const response = await fetch(file.uri)
      fileBlob = await response.blob()
    }

    const size = fileBlob?.size || file.size || 0

    if (size > 100 * 1024 * 1024) {
      Snackbar.error('File must be smaller than 100MB')
      setIsThumbnailUploading(false)
      return
    }

    const token = await getAccessToken()
    const uploadUrl = `${ApiRoot()}/${currentOrganization?.id}/${fosterId}/thumb`

    if (Platform.OS === 'web') {
      if (!fileBlob) {
        Snackbar.error(`There was a problem saving the image for ${name}`)
        setIsThumbnailUploading(false)
        return
      }

      const displayName = file.fileName || 'image.jpg'
      const body = new FormData()
      body.append('file', fileBlob, displayName)
      body.append('fileName', displayName)

      try {
        const response = await fetch(uploadUrl, {
          body,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          method: 'POST',
        })

        if (response.status !== 200) {
          if (response.status === 413) {
            Snackbar.error('File must be smaller than 100MB')
          } else {
            Snackbar.error(`There was a problem saving the image for ${name}`)
          }
          setIsThumbnailUploading(false)
          return
        }
      } catch {
        Snackbar.error(`There was a problem saving the image for ${name}`)
        setIsThumbnailUploading(false)
        return
      }

      await refreshFoster()
      setIsThumbnailUploading(false)
      refreshFosterList()
      return
    }

    const uploadResult = await FileSystem.uploadAsync(uploadUrl, file.uri, {
      uploadType: FileSystem.FileSystemUploadType.MULTIPART,
      // for some reason either this expo module, or the fastify multi-part handler, are overwriting the filename with a random UUID. we want to keep the original filename
      // so we're passing it as an extra field on the body
      parameters: {
        fileName: file.fileName || '',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (uploadResult.status !== 200) {
      if (uploadResult.status === 413) {
        Snackbar.error('File must be smaller than 100MB')
      } else {
        Snackbar.error(`There was a problem saving the image for ${name}`)
      }
      setIsThumbnailUploading(false)
      return
    }

    await refreshFoster()

    setIsThumbnailUploading(false)

    refreshFosterList()
  }

  return (
    <DragDropContentView
      onDrop={(event) => {
        if (disabled) {
          return
        }

        const file = event.assets[0]
        const uri = file.uri || file.base64

        if (!uri) {
          return
        }

        upload({
          fileName: file.fileName,
          size: 0,
          type: file.type,
          uri,
        })
      }}
      onEnter={() => !disabled && setIsFileDragging(true)}
      onExit={() => !disabled && setIsFileDragging(false)}
      style={styles.imagePressable}
    >
      <Pressable
        accessibilityRole="button"
        disabled={disabled}
        onPress={pickImage}
        style={styles.imagePressable}
        testID="foster-image-pressable"
      >
        {isFileDragging && <View style={styles.imageLoading} />}
        {isThumbnailUploading && (
          <UniActivityIndicator size="large" style={styles.imageLoading} />
        )}
        {!disabled && (
          <>
            <UniLinearGradient
              colors={[
                'rgba(0,0,0,0)',
                'rgba(0,0,0,0)',
                'rgba(0,0,0,0)',
                'rgba(0,0,0,0.8)',
              ]}
              dither
              end={{ x: 1, y: 1 }}
              locations={[0, 0.2, 0.8, 1]}
              start={{ x: 0, y: 0 }}
              style={styles.gradient}
            />
            <View style={styles.uploadIcon}>
              <UniIcon color={theme.colors.surface} size={30} source="upload" />
            </View>
          </>
        )}

        <FosterThumbnail
          blurhash={blurhash}
          species={species as Species}
          style={styles.image}
          thumbUri={thumbUri}
        />
      </Pressable>
    </DragDropContentView>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    gradient: {
      borderRadius: theme.tokens.imageBorderRadius,
      bottom: 0,
      height: '100%',
      position: 'absolute',
      right: 0,
      width: '100%',
      zIndex: 1,
    },
    image: {
      borderRadius: theme.tokens.imageBorderRadius,
      flex: 1,
    },
    imageLoading: {
      backgroundColor: 'rgba(0, 0, 0, 0.25)',
      borderRadius: theme.tokens.imageBorderRadius,
      bottom: 0,
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 20,
    },
    imagePressable: {
      alignSelf: 'center',
      height: 250,
      position: 'relative',
      width: 250,
    },
    uploadIcon: {
      bottom: 0,
      padding: theme.tokens.spacing[3],
      position: 'absolute',
      right: 0,
      zIndex: 2,
    },
  }
})

export default memo(FosterThumbnailEdit)
